import React, { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import staysApi from '../services/staysApi';
import ResponsiveVideo from './ResponsiveVideo';

function mapPawStayRowToStory(row) {
  const price =
    row.fromPrice != null && Number.isFinite(Number(row.fromPrice))
      ? `₹${Math.round(Number(row.fromPrice)).toLocaleString('en-IN')} onwards`
      : 'View stay';
  const image =
    row.coverImage ||
    'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80';
  const raw = (row.pawStoryVideoUrl && String(row.pawStoryVideoUrl).trim()) || '';
  const video =
    raw.startsWith('http://') || raw.startsWith('https://') ? raw : raw.startsWith('/') ? raw : `/${raw}`;
  return {
    stayId: row.stayId,
    title: row.title || 'Stay',
    location: [row.city, row.state].filter(Boolean).join(', ') || '—',
    price,
    image,
    video,
    duration: ''
  };
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

function isTouchLikeDevice() {
  if (typeof window === 'undefined') return false;
  return (
    isMobileViewport() ||
    'ontouchstart' in window ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  );
}

const PawStories = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedStory, setSelectedStory] = useState(null);
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(() => isTouchLikeDevice());
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showKeyboardHint, setShowKeyboardHint] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('up'); // 'up' or 'down'
  const scrollRef = useRef(null);
  const videoRef = useRef(null);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const endHandledRef = useRef(false);
  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [storiesError, setStoriesError] = useState(null);
  const stripScrollRaf = useRef(0);
  const playRetryTimerRef = useRef(null);
  const playRetryTimersRef = useRef([]);

  const clearPlayRetries = useCallback(() => {
    if (playRetryTimerRef.current) {
      clearTimeout(playRetryTimerRef.current);
      playRetryTimerRef.current = null;
    }
    playRetryTimersRef.current.forEach((id) => clearTimeout(id));
    playRetryTimersRef.current = [];
  }, []);

  const applyInlineMutedForAutoplay = useCallback((node) => {
    if (!node) return;
    node.muted = true;
    node.defaultMuted = true;
    node.setAttribute('muted', '');
    node.setAttribute('playsinline', '');
    node.setAttribute('webkit-playsinline', 'true');
    setIsMuted(true);
  }, []);

  const playReelVideo = useCallback((allowUnmuted = false) => {
    const node = videoRef.current;
    clearPlayRetries();

    const touchLike = isTouchLikeDevice();
    const mustMute = touchLike || !allowUnmuted;

    const attemptPlay = () => {
      const v = videoRef.current;
      if (!v) return false;
      if (mustMute) applyInlineMutedForAutoplay(v);
      else {
        v.muted = false;
        v.removeAttribute('muted');
        setIsMuted(false);
      }

      return v
        .play()
        .then(() => {
          setIsPlaying(true);
          return true;
        })
        .catch(() => {
          applyInlineMutedForAutoplay(v);
          return v
            .play()
            .then(() => {
              setIsPlaying(true);
              return true;
            })
            .catch(() => {
              setIsPlaying(false);
              return false;
            });
        });
    };

    const scheduleRetries = () => {
      [80, 200, 450, 900, 1500].forEach((delay) => {
        const id = window.setTimeout(() => {
          if (!videoRef.current || videoRef.current.paused) {
            attemptPlay();
          }
        }, delay);
        playRetryTimersRef.current.push(id);
      });
    };

    if (!node) {
      scheduleRetries();
      return;
    }

    if (mustMute) applyInlineMutedForAutoplay(node);

    const run = () => {
      attemptPlay().then((ok) => {
        if (!ok) scheduleRetries();
      });
    };

    if (node.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      run();
      return;
    }

    const onReady = () => {
      node.removeEventListener('canplay', onReady);
      node.removeEventListener('loadeddata', onReady);
      run();
    };
    node.addEventListener('canplay', onReady, { once: true });
    node.addEventListener('loadeddata', onReady, { once: true });
    playRetryTimerRef.current = window.setTimeout(run, 120);
    scheduleRetries();
  }, [applyInlineMutedForAutoplay, clearPlayRetries]);

  const setReelVideoRef = useCallback(
    (node) => {
      videoRef.current = node;
      if (!node || !selectedStory) return;
      if (isTouchLikeDevice()) applyInlineMutedForAutoplay(node);
      playReelVideo(false);
    },
    [selectedStory, applyInlineMutedForAutoplay, playReelVideo]
  );

  const syncStripPreviews = useCallback(() => {
    const root = scrollRef.current;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();
    const narrow =
      typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    const minFrac = narrow ? 0.12 : 0.35;
    root.querySelectorAll('video[data-paw-preview-video]').forEach((node) => {
      if (!(node instanceof HTMLVideoElement)) return;
      const vr = node.getBoundingClientRect();
      const overlap = Math.max(0, Math.min(vr.right, rootRect.right) - Math.max(vr.left, rootRect.left));
      const frac = vr.width > 0 ? overlap / vr.width : 0;
      if (frac >= minFrac) {
        if (node.preload === 'none') node.preload = 'metadata';
        node.play().catch(() => {});
      } else {
        node.pause();
        try {
          node.currentTime = 0;
        } catch (_) {
          /* ignore */
        }
      }
    });
  }, []);

  const scheduleStripPreviewSync = useCallback(() => {
    if (stripScrollRaf.current) cancelAnimationFrame(stripScrollRaf.current);
    stripScrollRaf.current = requestAnimationFrame(() => {
      stripScrollRaf.current = 0;
      syncStripPreviews();
    });
  }, [syncStripPreviews]);

  // Helper function to safely check if element is within a container
  const isWithinContainer = (element, selector) => {
    if (!element || !selector) return false;
    
    // Use closest if available
    if (element.closest) {
      return element.closest(selector);
    }
    
    // Fallback: traverse up the DOM tree
    let current = element;
    while (current && current !== document) {
      if (current.matches && current.matches(selector)) {
        return current;
      }
      // Also check tagName for simple selectors like 'button'
      if (selector === 'button' && current.tagName === 'BUTTON') {
        return current;
      }
      current = current.parentElement;
    }
    return false;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStoriesLoading(true);
        const rows = await staysApi.getPawStoryStays();
        if (cancelled) return;
        setStories((rows || []).map(mapPawStayRowToStory));
        setStoriesError(null);
      } catch (e) {
        console.error('Paw stories load failed:', e);
        if (!cancelled) {
          setStories([]);
          setStoriesError(e);
        }
      } finally {
        if (!cancelled) setStoriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Retry play when reel opens or index changes (after modal video mounts).
  useEffect(() => {
    if (!selectedStory) return undefined;
    playReelVideo(false);
    return () => clearPlayRetries();
  }, [selectedStory, currentReelIndex, playReelVideo, clearPlayRetries]);

  const handleReelVideoSrcReady = useCallback(() => {
    if (selectedStory) playReelVideo(false);
  }, [selectedStory, playReelVideo]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (selectedStory) {
      // Store original body styles
      const originalStyle = window.getComputedStyle(document.body);
      const originalOverflow = originalStyle.overflow;
      const originalPosition = originalStyle.position;
      const originalTop = originalStyle.top;
      
      // Get current scroll position
      const scrollY = window.scrollY;
      
      // Apply scroll prevention
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Add CSS class for additional scroll prevention
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      
      // Add global scroll prevention
      const preventScroll = (e) => {
        // Only prevent scroll if it's not within the modal
        const isWithinModal = isWithinContainer(e.target, '.story-modal-container');
        if (!isWithinModal) {
          e.preventDefault();
        }
      };
      
      // Add event listeners to prevent scroll
      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('scroll', preventScroll, { passive: false });
      
      // Cleanup: restore scrolling when modal closes
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.body.classList.remove('modal-open');
        document.documentElement.classList.remove('modal-open');
        
        // Remove event listeners
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventScroll);
        document.removeEventListener('scroll', preventScroll);
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [selectedStory]);

  // Only preview videos visible inside the horizontal strip (saves mobile RAM vs playing all).
  useEffect(() => {
    if (storiesLoading || stories.length === 0) return undefined;

    let io = null;
    let cancelled = false;

    const attach = () => {
      if (cancelled) return;
      if (io) {
        io.disconnect();
        io = null;
      }
      const root = scrollRef.current;
      if (!root) return;
      const nodes = root.querySelectorAll('video[data-paw-preview-video]');
      if (nodes.length === 0) return;

      io = new IntersectionObserver(
        () => {
          syncStripPreviews();
        },
        {
          root,
          rootMargin: '0px',
          threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.33, 0.42, 0.5, 0.6, 0.75, 1]
        }
      );
      nodes.forEach((el) => io.observe(el));
      syncStripPreviews();
      setTimeout(syncStripPreviews, 200);
      setTimeout(syncStripPreviews, 500);
    };

    let rafOuter;
    let rafInner;
    rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(attach);
    });
    const retryTimer = setTimeout(attach, 120);

    window.addEventListener('resize', attach);
    return () => {
      cancelled = true;
      if (rafOuter) cancelAnimationFrame(rafOuter);
      if (rafInner) cancelAnimationFrame(rafInner);
      clearTimeout(retryTimer);
      window.removeEventListener('resize', attach);
      if (io) io.disconnect();
    };
  }, [stories, storiesLoading, syncStripPreviews]);

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (!container || stories.length === 0) return;
    const scrollAmount = 300;

    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      setCurrentSlide(Math.max(0, currentSlide - 1));
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setCurrentSlide(Math.min(Math.max(0, stories.length - 1), currentSlide + 1));
    }
  };

  const openStoryReel = (story, index) => {
    const touchLike = isTouchLikeDevice();
    flushSync(() => {
      setIsMuted(touchLike);
      setSelectedStory(story);
      setCurrentReelIndex(index);
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    });

    // iOS Safari: play() must run in the same user-gesture turn as the card tap.
    playReelVideo(false);

    if (window.innerWidth >= 768) {
      setShowKeyboardHint(true);
      setTimeout(() => setShowKeyboardHint(false), 3000);
    }
  };

  const closeStoryReel = useCallback(() => {
    endHandledRef.current = false;
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      } catch (_) {
        /* ignore */
      }
    }
    setSelectedStory(null);
    setCurrentReelIndex(0);
    setIsPlaying(false);
    clearPlayRetries();
    setProgress(0);
    setCurrentTime(0);
    setShowKeyboardHint(false);
  }, [clearPlayRetries]);

  const goToNextReel = useCallback(() => {
    if (currentReelIndex < stories.length - 1) {
      setIsTransitioning(true);
      setSlideDirection('up');
      endHandledRef.current = false;
      const nextIndex = currentReelIndex + 1;

      flushSync(() => {
        setCurrentReelIndex(nextIndex);
        setSelectedStory(stories[nextIndex]);
        setProgress(0);
        setCurrentTime(0);
      });
      playReelVideo(false);

      setTimeout(() => {
        setIsTransitioning(false);
        playReelVideo(false);
      }, 300);
    } else {
      closeStoryReel();
    }
  }, [currentReelIndex, stories, closeStoryReel, playReelVideo]);

  const goToPrevReel = useCallback(() => {
    if (currentReelIndex > 0) {
      setIsTransitioning(true);
      setSlideDirection('down');
      endHandledRef.current = false;
      const prevIndex = currentReelIndex - 1;

      flushSync(() => {
        setCurrentReelIndex(prevIndex);
        setSelectedStory(stories[prevIndex]);
        setProgress(0);
        setCurrentTime(0);
      });
      playReelVideo(false);

      setTimeout(() => {
        setIsTransitioning(false);
        playReelVideo(false);
      }, 300);
    }
  }, [currentReelIndex, stories, playReelVideo]);

  useEffect(() => {
    endHandledRef.current = false;
  }, [currentReelIndex]);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  }, [isMuted]);

  const togglePlayPause = useCallback((e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().catch(error => {
          console.log('Play failed:', error);
        });
        setIsPlaying(true);
      }
    }
  }, [isPlaying]);

  // Convert story duration string (like "0:20") to seconds
  const parseDurationToSeconds = (durationString) => {
    if (!durationString) return 0;
    const parts = durationString.split(':');
    if (parts.length !== 2) return 0;
    const minutes = parseInt(parts[0], 10) || 0;
    const seconds = parseInt(parts[1], 10) || 0;
    return minutes * 60 + seconds;
  };

  const getTotalDurationSeconds = () => {
    const parsed = parseDurationToSeconds(stories[currentReelIndex]?.duration);
    if (parsed > 0) return parsed;
    const v = videoRef.current;
    if (v && v.duration && Number.isFinite(v.duration) && v.duration > 0) return v.duration;
    return 0;
  };

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      setCurrentTime(current);
      const total = getTotalDurationSeconds();
      if (total > 0) {
        setProgress((current / total) * 100);
        if (current >= total - 0.08) {
          handleVideoEnd();
        }
      }
    }
  };

  // Note: All videos are set to max 20 seconds for better user experience

  // Format time from seconds to mm:ss
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Calculate remaining time
  const getRemainingTime = () => {
    const total = getTotalDurationSeconds();
    const remaining = total - currentTime;
    return remaining > 0 ? remaining : 0;
  };

  const handleVideoEnd = () => {
    if (endHandledRef.current) return;
    endHandledRef.current = true;
    setProgress(100);
    const total = getTotalDurationSeconds();
    setCurrentTime(total > 0 ? total : currentTime);
    setTimeout(() => {
      goToNextReel();
      endHandledRef.current = false;
    }, 500);
  };

  // Touch handlers for reel navigation
  const handleTouchStart = (e) => {
    // Don't interfere with button clicks
    if (isWithinContainer(e.target, 'button')) {
      return;
    }
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    // Don't interfere with button clicks
    if (isWithinContainer(e.target, 'button')) {
      return;
    }
    
    touchEndY.current = e.changedTouches[0].clientY;
    const diff = touchStartY.current - touchEndY.current;
    
    // If swipe distance is more than 50px
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swiped up - go to next reel
        goToNextReel();
      } else {
        // Swiped down - go to previous reel
        goToPrevReel();
      }
    }
  };

  const handleReelClick = (e) => {
    // All devices - just handle play/pause on video click
    togglePlayPause();
  };

  // Keyboard navigation for desktop
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedStory) return; // Only work when reel is open
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevReel();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNextReel();
          break;
        case 'ArrowUp':
          e.preventDefault();
          goToPrevReel();
          break;
        case 'ArrowDown':
          e.preventDefault();
          goToNextReel();
          break;
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'Escape':
          e.preventDefault();
          closeStoryReel();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          toggleMute();
          break;
      }
    };

    if (selectedStory) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedStory, currentReelIndex, goToPrevReel, goToNextReel, togglePlayPause, closeStoryReel, toggleMute]);

  return (
    <>
      <section className="py-[32px] md:py-[60px]">
        <div className="px-[0px] md:px-[60px] lg:px-[120px]">
          {/* Section Header */}
          <div className="flex items-center justify-between mb-[20px]">
            <div className="flex  px-[16px] items-center">
              <h2 className="text-[20px] font-medium text-black mr-[10px]">Paw Stories</h2>
              <span className="flex items-center space-x-[3px]">
                {/* Fire Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
  <g clip-path="url(#clip0_174_2370)">
    <path d="M4 21.1396L20 17.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M20 21.1396L4 17.1396" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12 15.1396C13.0609 15.1396 14.0783 14.7182 14.8284 13.9681C15.5786 13.2179 16 12.2005 16 11.1396C16 8.13965 14 8.13965 14 3.13965C10 5.13965 8 8.13965 8 11.1396C8 12.2005 8.42143 13.2179 9.17157 13.9681C9.92172 14.7182 10.9391 15.1396 12 15.1396Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_174_2370">
      <rect width="24" height="24" fill="white" transform="translate(0 0.139648)"/>
    </clipPath>
  </defs>
</svg>
                {/* Bone Icon */}
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                  <g clipPath="url(#clip0_174_2237)">
                    <path d="M14.9999 3.13965C15.7955 3.13965 16.5586 3.45572 17.1212 4.01833C17.6838 4.58094 17.9999 5.344 17.9999 6.13965C18.4932 6.13954 18.979 6.2611 19.4141 6.49356C19.8493 6.72602 20.2204 7.06221 20.4945 7.47234C20.7687 7.88247 20.9375 8.35389 20.986 8.84484C21.0345 9.33579 20.9612 9.83112 20.7725 10.287C20.5838 10.7428 20.2856 11.1451 19.9044 11.4581C19.5231 11.7712 19.0705 11.9854 18.5867 12.0818C18.1028 12.1782 17.6027 12.1538 17.1306 12.0107C16.6584 11.8677 16.2289 11.6104 15.8799 11.2616L11.1219 16.0196C11.4653 16.3633 11.7201 16.7853 11.8644 17.2492C12.0086 17.7131 12.0381 18.2051 11.9502 18.6829C11.8623 19.1607 11.6596 19.61 11.3597 19.9922C11.0598 20.3744 10.6715 20.678 10.2283 20.877C9.78506 21.0759 9.30015 21.1643 8.81524 21.1344C8.33033 21.1045 7.85993 20.9573 7.44448 20.7055C7.02903 20.4536 6.68096 20.1046 6.4302 19.6885C6.17944 19.2724 6.03349 18.8016 6.0049 18.3166V18.1396H5.8289C5.34373 18.1112 4.87274 17.9653 4.45643 17.7146C4.04013 17.4638 3.69098 17.1157 3.43903 16.7001C3.18707 16.2845 3.03986 15.8139 3.01005 15.3288C2.98023 14.8437 3.06871 14.3587 3.26788 13.9154C3.46705 13.472 3.77093 13.0838 4.15339 12.7839C4.53586 12.484 4.98544 12.2816 5.46347 12.194C5.94151 12.1063 6.43367 12.1361 6.89763 12.2808C7.36158 12.4256 7.78344 12.6808 8.1269 13.0246L12.8849 8.26665C12.4652 7.84721 12.1793 7.31272 12.0634 6.73078C11.9474 6.14885 12.0067 5.5456 12.2336 4.99734C12.4605 4.44907 12.8449 3.98042 13.3382 3.65064C13.8315 3.32086 14.4115 3.14478 15.0049 3.14465L14.9999 3.13965Z" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_174_2237">
                      <rect width="24" height="24" fill="white" transform="translate(0 0.139648)"/>
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/stays')}
              className="text-blue-600 text-sm font-normal underline shrink-0 px-[16px] md:pr-0 hover:opacity-80"
            >
              View All
            </button>
          </div>

          {/* Horizontal Scrolling Container */}
          <div className="relative">
            {storiesLoading && (
              <p className="px-[16px] text-sm text-gray-500">Loading paw stories…</p>
            )}
            {!storiesLoading && storiesError && (
              <p className="px-[16px] text-sm text-red-600">Could not load stories. Please try again later.</p>
            )}
            {!storiesLoading && !storiesError && stories.length === 0 && (
              <p className="px-[16px] text-sm text-gray-500">
                No reels yet. Add a Paw Story video URL to a stay in Admin to show it here.
              </p>
            )}
            <div 
              ref={scrollRef}
              onScroll={scheduleStripPreviewSync}
              onTouchEnd={scheduleStripPreviewSync}
              className="flex gap-[8px] px-[16px] overflow-x-auto scrollbar-hide horizontal-scroll"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {stories.map((story, index) => (
                <div 
                  key={story.stayId}
                  className="flex-shrink-0 w-[290px] bg-white rounded-[20px] overflow-hidden shadow-sm cursor-pointer"
                  onClick={() => openStoryReel(story, index)}
                >
                  <div className="relative rounded-[24px] w-[290px] h-[500px] bg-neutral-200">
                    <ResponsiveVideo
                      data-paw-preview-video
                      src={story.video}
                      deliveryWidth={580}
                      poster={story.image}
                      width={290}
                      height={500}
                      className="w-full h-full object-cover pointer-events-none"
                      muted
                      loop
                      playsInline
                      preload={index < 2 ? 'metadata' : 'none'}
                      disablePictureInPicture
                      aria-label={story.title ? `Preview: ${story.title}` : 'Reel preview'}
                    />

                    {/* Content */}
                  <div className="absolute bottom-[4px] left-[4px] right-[4px] rounded-[20px] bg-white p-[12px]">
                    <h3 className="font-medium text-gray-900 text-[18px] mb-[8px] leading-[1.1]">
                      {story.title}
                    </h3>
                    <p className="text-[#bebebe] text-[12px] leading-none mb-[8px]">{story.location}</p>
                    
                    {/* Price and Button */}
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[14px] text-gray-900">{story.price}</span>
                      
                    </div>
                  </div>
                  </div>
                  
                  
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

       {/* Story Reel Modal */}
       {selectedStory && (
         <div 
           className="fixed inset-0 z-50 bg-black"
           onScroll={(e) => e.stopPropagation()}
           onWheel={(e) => e.stopPropagation()}
           onTouchMove={(e) => e.stopPropagation()}
         >
           <div 
             className="story-modal-container relative mx-auto h-[100dvh] w-full max-w-none overflow-hidden bg-black md:max-w-[450px]"
             onTouchStart={handleTouchStart}
             onTouchEnd={handleTouchEnd}
             onScroll={(e) => e.stopPropagation()}
             onWheel={(e) => e.stopPropagation()}
             onTouchMove={(e) => e.stopPropagation()}
           >
            {/* Video Timeline */}
            <div className={`absolute top-[25px] left-[16px] right-[16px] z-10 transition-all duration-300 ease-out ${
              isTransitioning ? 'opacity-0 transform -translate-y-2' : 'opacity-100 transform translate-y-0'
            }`}>
              <div className="w-full h-1 bg-white overflow-hidden">
                <div 
                  className="h-full bg-[#FF5E4C] transition-all duration-100 ease-linear"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Close Button */}
            <button 
              onClick={closeStoryReel}
              className={`absolute top-[40px] left-[16px] z-10 text-white text-2xl transition-all duration-300 ease-out ${
                isTransitioning ? 'opacity-0 transform -translate-y-2' : 'opacity-100 transform translate-y-0'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                <g clip-path="url(#clip0_174_12747)">
                  <path d="M21 7L7 21" stroke="white" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7 7L21 21" stroke="white" stroke-width="2.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_174_12747">
                    <rect width="28" height="28" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </button>

            {/* Duration - Dynamic Countdown */}
            <div className={`absolute top-[47px] right-[16px] z-10 text-white text-[10px] leading-none bg-black bg-opacity-50 p-[4px] rounded-[4px] transition-all duration-300 ease-out ${
              isTransitioning ? 'opacity-0 transform -translate-y-2' : 'opacity-100 transform translate-y-0'
            }`}>
              {formatTime(getRemainingTime())}
            </div>

            {/* Keyboard Hint for Desktop */}
            {showKeyboardHint && (
              <div className="hidden md:block absolute top-20 left-1/2 transform -translate-x-1/2 z-20 bg-black bg-opacity-80 text-white text-xs px-3 py-2 rounded-lg animate-pulse">
                Use ← → arrows or space bar to navigate
              </div>
            )}

            {/* Video Background — no transform when idle (iOS Safari mis-renders video inside transformed layers) */}
            <div className="story-reel-video-layer absolute inset-0 overflow-hidden">
              <div
                className={`absolute inset-0 transition-opacity duration-300 ease-out md:transition-all ${
                  isTransitioning
                    ? slideDirection === 'up'
                      ? 'opacity-0 md:translate-y-full'
                      : 'opacity-0 md:-translate-y-full'
                    : 'opacity-100'
                }`}
              >
                <ResponsiveVideo
                  ref={setReelVideoRef}
                  src={stories[currentReelIndex].video}
                  deliveryWidth={720}
                  poster={stories[currentReelIndex].image}
                  className="story-reel-video absolute inset-0 h-full w-full cursor-pointer object-cover object-center"
                  muted={isMuted}
                  autoPlay
                  playsInline
                  preload="auto"
                  disablePictureInPicture
                  controlsList="nodownload noplaybackrate"
                  onDeliveredSrcReady={handleReelVideoSrcReady}
                  onError={() => {
                    console.warn('Paw story reel failed to load:', stories[currentReelIndex]?.video);
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onTimeUpdate={handleVideoProgress}
                  onEnded={handleVideoEnd}
                  onClick={handleReelClick}
                />
              </div>
            </div>

              {/* Video click area - handles play/pause only */}
              <div className="absolute inset-0 md:hidden">
                {/* Entire video area is clickable for play/pause */}
              </div>

              {/* Desktop Navigation Arrows */}
              <div className="hidden md:block">
                {/* Left Arrow - Previous */}
                {currentReelIndex > 0 && (
                  <button
                    onClick={goToPrevReel}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-50 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
                    title="Previous video (← or ↑)"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}

                {/* Right Arrow - Next */}
                {currentReelIndex < stories.length - 1 && (
                  <button
                    onClick={goToNextReel}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black bg-opacity-50 hover:bg-opacity-80 text-white p-3 rounded-full transition-all duration-200 hover:scale-110 shadow-lg hover:shadow-xl"
                    title="Next video (→ or ↓)"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}

                {/* Desktop video click area - handles play/pause only */}
                <div className="absolute z-100 inset-0">
                  {/* Entire video area is clickable for play/pause */}
                </div>
              </div>
              
              {/* Bottom Card */}
              <div className={`absolute bottom-[20px] left-[20px] right-[20px] z-20 bg-white rounded-[20px] p-[12px] transition-opacity duration-300 ease-out ${
                isTransitioning ? 'opacity-0' : 'opacity-100'
              }`}>
                <h3 className="font-medium text-black text-[18px] leading-[1.1] mb-[8px]">
                  {stories[currentReelIndex].title}
                </h3>
                <p className="text-[#bebebe] text-[12px] leading-none mb-[8px]">{stories[currentReelIndex].location}</p>
                <p className="text-gray-900 text-[14px] font-medium mb-[12px]">{stories[currentReelIndex].price}</p>
                <div className='w-full border-b border-dashed border-[#f0f0f0] mb-[12px]'></div>
                
                {/* Buttons */}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    className="flex-1 bg-white text-red-500 text-[18px] font-medium"
                    onClick={() => {
                      const sid = stories[currentReelIndex]?.stayId;
                      closeStoryReel();
                      if (sid) navigate(`/property/${sid}`);
                    }}
                  >
                    View Stay
                  </button>
                  <button
                    type="button"
                    className="flex-1 relative overflow-hidden bg-black text-white p-[18px] rounded-[12px] text-[16px] font-medium flex items-center justify-center"
                    onClick={() => {
                      const sid = stories[currentReelIndex]?.stayId;
                      closeStoryReel();
                      if (sid) navigate(`/property/${sid}/rooms`);
                    }}
                  >
                    <span>Book Stay</span>
                    <svg className='absolute right-[10%] bottom-[-5%]' xmlns="http://www.w3.org/2000/svg" width="28" height="32" viewBox="0 0 28 32" fill="none">
                        <path d="M17.8311 9.07719C17.4572 9.07719 17.076 8.98554 16.7388 8.90489C15.8407 8.68863 13.7404 8.18645 14.2425 6.0861C14.4368 5.27235 14.8803 4.56307 15.4907 4.08838C16.1395 3.58254 16.9293 3.39377 17.6643 3.56972C19.1342 3.92161 19.9919 5.62424 19.5759 7.36353C19.246 8.74177 18.5495 9.07719 17.8311 9.07719ZM17.1127 4.97179C16.7974 4.97179 16.5335 5.1349 16.3905 5.2467C16.0478 5.51428 15.7839 5.94498 15.6684 6.42883C15.5621 6.87052 15.5768 7.11977 17.0796 7.479C17.3142 7.53581 17.8073 7.65129 17.9191 7.60547C17.9191 7.60547 18.0309 7.51565 18.1482 7.02264C18.3718 6.0861 17.9942 5.15873 17.3216 4.99745C17.2501 4.98095 17.1805 4.97179 17.1127 4.97179Z" fill="#808080"/>
                        <path d="M9.68556 8.23943C9.35016 8.23943 9.01292 8.19179 8.70501 8.03967C8.12952 7.75375 7.79595 7.18926 7.68782 6.3187C7.58885 5.52144 7.74829 4.73336 8.13684 4.09922C8.55287 3.42293 9.20351 2.98305 9.92562 2.89508C10.6422 2.80527 11.3845 3.07104 11.9545 3.62454C12.4878 4.14321 12.8342 4.86897 12.9332 5.66622C13.0707 6.76588 12.5685 7.35605 12.1213 7.65662C11.6173 7.99752 10.9685 8.1148 10.3985 8.18627C10.1694 8.2156 9.92748 8.23943 9.68189 8.23943H9.68556ZM10.2079 4.34113C10.1749 4.34113 10.1419 4.34297 10.1071 4.34847C9.72768 4.39613 9.49126 4.69302 9.38679 4.8653C9.17419 5.2117 9.08621 5.67356 9.14302 6.13542C9.18334 6.45799 9.26402 6.67791 9.35749 6.72556C9.5371 6.81537 10.0338 6.75306 10.2207 6.72923C10.5946 6.68341 11.0546 6.60828 11.302 6.43967C11.3882 6.38102 11.5348 6.28205 11.4798 5.84402C11.423 5.38216 11.2232 4.95329 10.9318 4.67104C10.7998 4.54275 10.5396 4.3393 10.2079 4.3393V4.34113Z" fill="#808080"/>
                        <path d="M4.47404 12.411C3.61997 12.411 3.127 11.8703 2.90706 11.5441C2.08782 10.3234 2.36087 8.69777 3.51734 7.92068C4.67382 7.14542 6.283 7.50465 7.10408 8.72527C7.52378 9.35024 7.63008 9.94955 7.42298 10.5085C7.21221 11.0785 6.72286 11.4616 6.31965 11.7328C5.57371 12.2332 4.96522 12.411 4.47404 12.411ZM4.87729 8.97452C4.68668 8.97452 4.49787 9.02767 4.33475 9.13581C3.84907 9.46204 3.75378 10.175 4.124 10.7248C4.22297 10.8733 4.45757 11.216 5.50224 10.514C5.91278 10.2391 6.02091 10.0723 6.0484 9.9972C6.09056 9.88173 5.98428 9.68563 5.88714 9.54084C5.64155 9.17612 5.253 8.97269 4.87729 8.97269V8.97452Z" fill="#808080"/>
                        <path d="M22.6024 13.142C21.9059 13.142 21.2864 12.726 20.8979 12.4657C20.2729 12.0442 18.3668 10.7649 19.6827 8.80751C20.7182 7.26616 22.6188 6.72183 23.9183 7.59606C25.2177 8.47028 25.434 10.4332 24.3985 11.9745C23.8047 12.8598 23.1779 13.142 22.6024 13.142ZM22.5107 8.64075C21.9536 8.64075 21.3139 9.00546 20.8997 9.62311C20.59 10.085 20.491 10.424 21.7153 11.2469C22.6225 11.8554 22.7453 11.8059 23.1815 11.1553C23.757 10.2994 23.7185 9.22539 23.1009 8.81118C22.9286 8.69572 22.7252 8.64075 22.5107 8.64075Z" fill="#808080"/>
                        <path d="M22.7728 42.6478H4.44512C4.24351 42.6478 4.05109 42.5653 3.9118 42.4187C3.77434 42.2721 3.70102 42.0742 3.71201 41.8744C3.71934 41.7424 4.44328 28.6327 4.44328 22.854C4.44328 18.9942 3.8055 16.4668 3.36747 15.1838C2.50607 14.6817 1.7473 13.9614 1.16082 13.089C-0.642625 10.404 -0.307261 7.00971 1.90672 5.5215C2.92024 4.84155 4.20502 4.65826 5.48979 4.98266C5.75004 2.43695 7.38671 0.446562 9.5842 0.171647C11.2777 -0.0409534 12.9088 0.809457 13.9828 2.3783C15.198 1.17601 16.7686 0.660999 18.2715 1.02205C20.0181 1.44176 21.3157 2.98312 21.6915 4.99C22.9909 4.6436 24.2702 4.82689 25.3075 5.52334C27.5215 7.01154 27.8568 10.4058 26.0534 13.0908C25.4669 13.965 24.7082 14.6835 23.8468 15.1857C23.4069 16.4668 22.7691 18.9923 22.7691 22.8558C22.7691 28.6327 23.493 41.7443 23.5004 41.8762C23.5114 42.0778 23.4399 42.2739 23.3006 42.4206C23.1613 42.5672 22.9689 42.6497 22.7673 42.6497L22.7728 42.6478ZM5.21854 41.1816H21.9975C21.8417 38.2638 21.3047 27.8519 21.3047 22.854C21.3047 18.4901 22.0928 15.6988 22.562 14.4232C22.6243 14.2528 22.749 14.1117 22.9121 14.0274C23.6635 13.6352 24.3288 13.0285 24.8383 12.2716C26.1909 10.2592 26.0351 7.77581 24.4919 6.7403C23.6415 6.17031 22.5052 6.13913 21.3726 6.65781C21.1508 6.76044 20.8924 6.74395 20.6816 6.61566C20.4708 6.48736 20.3425 6.26193 20.3334 6.01817C20.2674 4.21107 19.3015 2.77602 17.9288 2.44795C16.7063 2.15471 15.3757 2.77785 14.4612 4.07728C14.3109 4.28988 14.0616 4.409 13.7941 4.38517C13.532 4.36135 13.3047 4.19824 13.1947 3.95998C12.4671 2.37464 11.1237 1.46009 9.76381 1.62688C8.06483 1.83948 6.81672 3.74922 6.92486 5.97786C6.93768 6.23445 6.81305 6.47821 6.60045 6.623C6.38602 6.76595 6.11112 6.7861 5.88019 6.67613C4.73654 6.13913 3.5874 6.16114 2.72234 6.7403C1.17915 7.77764 1.02334 10.2592 2.37592 12.2716C2.88543 13.0285 3.55072 13.637 4.30216 14.0274C4.46344 14.1117 4.58805 14.2528 4.6522 14.4251C5.12139 15.7007 5.9095 18.4938 5.9095 22.854C5.9095 27.8519 5.37249 38.2638 5.21671 41.1816H5.21854Z" fill="#808080"/>
                        <path d="M16.1752 19.9214C15.3541 19.9214 14.9986 19.7217 14.6485 19.4798C14.4304 19.3295 14.2251 19.1883 13.2428 19.1883C12.2604 19.1883 12.0552 19.3295 11.8371 19.4798C11.487 19.7217 11.1314 19.9214 10.3104 19.9214C9.295 19.9214 7.37793 19.0014 7.37793 15.5228C7.37793 12.2733 10.5871 9.65796 13.2428 9.65796C15.8985 9.65796 19.1076 12.2733 19.1076 15.5228C19.1076 19.0014 17.1906 19.9214 16.1752 19.9214ZM13.2428 17.7221C14.5734 17.7221 15.0444 17.9714 15.4806 18.2738C15.6602 18.3984 15.7427 18.4552 16.1752 18.4552C16.5839 18.4552 17.6414 17.9274 17.6414 15.5228C17.6414 12.9716 15.0132 11.1242 13.2428 11.1242C11.4723 11.1242 8.84414 12.9716 8.84414 15.5228C8.84414 17.9274 9.90165 18.4552 10.3104 18.4552C10.7411 18.4552 10.8235 18.3984 11.005 18.2738C11.443 17.9732 11.9122 17.7221 13.2428 17.7221Z" fill="#808080"/>
                      </svg>
                  </button>
                </div>
              </div>

              {/* Audio/Sound Control */}
              <div className="absolute bottom-32 right-4 z-10">
                <button 
                  onClick={toggleMute}
                  className="bg-black bg-opacity-50 text-white p-3 rounded-full"
                >
                  {isMuted ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.82L4.22 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.22l4.163-3.82a1 1 0 011.617.82zM7.293 7.293a1 1 0 011.414 0L10 8.586l1.293-1.293a1 1 0 111.414 1.414L11.414 10l1.293 1.293a1 1 0 01-1.414 1.414L10 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L8.586 10 7.293 8.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.82L4.22 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.22l4.163-3.82a1 1 0 011.617.82zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Share Button */}
              <div className="absolute bottom-44 right-4 z-10">
                <button className="bg-black bg-opacity-50 text-white p-3 rounded-full">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </button>
              </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PawStories; 